import React, { useState, useEffect, useCallback } from 'react';
import { IoIosCloseCircle } from 'react-icons/io';
import { MdDragIndicator, MdSwapHoriz } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import { uploadImagecertifates } from '../../../Redux/Actions/Action';
import {
    Box,
    Button,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Chip,
    Skeleton,
    CircularProgress,
    TextField,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import { validateFile } from '../../../utils/comman-function';

const REDIRECT_OPTIONS = [
    { label: 'Recharge Pack', value: 'recharge_pack' },
    { label: 'Connect Page', value: 'connect_page' },
    { label: 'Shastriji', value: 'shastriji' },
    { label: 'Services', value: 'services' },
    { label: 'Pooja Listing', value: 'pooja_listing' },
    { label: 'Pooja Details', value: 'pooja_details' },
    // { label: 'Custom URL', value: 'custom' },
];

/* ─────────────────────────────────────────────
   Reusable Banner Card (compact for side-by-side)
   ───────────────────────────────────────────── */
function BannerCard({
    title,
    bannerIndex,
    bannerImage,
    isVisible,
    redirectScreen,
    loading,
    onImageUpload,
    onRemoveImage,
    onVisibilityToggle,
    onRedirectChange,
    onDrop,
    customUrl = '',
    onCustomUrlChange,
    showPoojaSelector = false,
    poojaList = [],
    selectedPoojaId = '',
    onPoojaSelect,
    poojaListLoading = false,
    showCustomUrlOption = false,
    sizeHint = '',
    hideRedirect = false,
    // Position Drag & Drop props
    isDraggable = false,
    onPositionDragStart,
    onPositionDragEnd,
    onPositionDragOver,
    onPositionDragLeave,
    onPositionDrop,
    isBeingDragged = false,
    isDragOver = false
}) {
    const [isDragging, setIsDragging] = useState(false);
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDropInternal = (e) => { e.preventDefault(); setIsDragging(false); onDrop(e); };

    const handleRootDragOver = (e) => {
        // Drag over is ONLY allowed if this card has an image AND is visible (Live)!
        if (isDraggable && onPositionDragOver && bannerImage && isVisible) {
            onPositionDragOver(e);
        }
    };

    const handleRootDragLeave = (e) => {
        if (isDraggable && onPositionDragLeave) {
            onPositionDragLeave(e);
        }
    };

    const handleRootDrop = (e) => {
        const hasFiles = e.dataTransfer.types?.includes('Files') && e.dataTransfer.files?.length > 0;
        if (hasFiles) {
            if (onDrop) onDrop(e);
            return;
        }
        if (isDraggable && onPositionDrop) {
            if (!bannerImage) {
                toast.error('Drop is only allowed on filled slots with images.');
                return;
            }
            if (!isVisible) {
                toast.error('Cannot reorder: Drop is only allowed on Live banners.');
                return;
            }
            onPositionDrop(e);
        }
    };

    return (
        <Box
            onDragOver={handleRootDragOver}
            onDragLeave={handleRootDragLeave}
            onDrop={handleRootDrop}
            sx={{
                border: isDragOver ? '2px dashed #E7B437 !important' : '1.5px solid #eaeaea',
                borderRadius: '16px',
                padding: 0,
                backgroundColor: isDragOver ? '#fffef5' : '#fff',
                overflow: 'hidden',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: isBeingDragged ? 0.45 : 1,
                transform: isDragOver ? 'scale(1.02)' : 'none',
                boxShadow: isDragOver ? '0 0 20px rgba(231,180,55,0.35)' : '0 2px 8px rgba(0,0,0,0.03)',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(231,180,55,0.12)',
                    borderColor: '#E7B437',
                    transform: isDragOver ? 'scale(1.02)' : 'translateY(-2px)',
                },
            }}
        >
            {/* Card Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 100%)',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f2e7c4',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        draggable={isDraggable && Boolean(bannerImage) && Boolean(isVisible)}
                        onDragStart={(bannerImage && isVisible) ? onPositionDragStart : undefined}
                        onDragEnd={onPositionDragEnd}
                        title={
                            (isDraggable && bannerImage && isVisible)
                                ? "Drag to reorder position in this section"
                                : (isDraggable && bannerImage && !isVisible)
                                    ? "Disabled banners cannot be reordered (Live only)"
                                    : undefined
                        }
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.3,
                            cursor: (isDraggable && bannerImage && isVisible) ? 'grab' : (isDraggable && bannerImage && !isVisible) ? 'not-allowed' : 'default',
                            p: (isDraggable && bannerImage) ? '2px 5px 2px 3px' : 0,
                            borderRadius: '8px',
                            background: (isDraggable && bannerImage && isVisible)
                                ? 'rgba(231,180,55,0.2)'
                                : (isDraggable && bannerImage && !isVisible)
                                    ? 'rgba(0,0,0,0.05)'
                                    : 'transparent',
                            border: (isDraggable && bannerImage && isVisible)
                                ? '1px dashed #E7B437'
                                : (isDraggable && bannerImage && !isVisible)
                                    ? '1px dashed #bbb'
                                    : 'none',
                            opacity: (isDraggable && bannerImage && !isVisible) ? 0.6 : 1,
                            userSelect: 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': (isDraggable && bannerImage && isVisible) ? {
                                background: 'rgba(231,180,55,0.32)',
                                transform: 'scale(1.04)'
                            } : {},
                            '&:active': (isDraggable && bannerImage && isVisible) ? { cursor: 'grabbing' } : {}
                        }}
                    >
                        {(isDraggable && bannerImage) && (
                            <MdDragIndicator style={{ fontSize: '16px', color: isVisible ? '#c4920e' : '#888' }} />
                        )}
                        <Box
                            sx={{
                                width: 28, height: 28, borderRadius: '8px',
                                background: isVisible ? 'linear-gradient(135deg, #E7B437, #d4a02e)' : '#9e9e9e',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '13px', fontWeight: 800, color: '#fff',
                                boxShadow: isVisible ? '0 2px 6px rgba(231,180,55,0.35)' : 'none',
                            }}
                        >
                            {bannerIndex}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#2b2b2b', lineHeight: 1.2 }}>
                            {title}
                        </Typography>
                        {sizeHint && (
                            <Typography sx={{ fontSize: '9.5px', color: '#777', fontWeight: 500, mt: 0.2 }}>
                                {sizeHint}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {/* Status Badge */}
                <Chip
                    label={isVisible ? '● Live' : '● Off'}
                    size="small"
                    sx={{
                        fontWeight: 700, fontSize: '10px', height: '22px',
                        backgroundColor: isVisible ? 'rgba(46,125,50,0.1)' : 'rgba(158,158,158,0.12)',
                        color: isVisible ? '#2e7d32' : '#888',
                        border: `1px solid ${isVisible ? 'rgba(46,125,50,0.25)' : 'rgba(158,158,158,0.25)'}`,
                    }}
                />
            </Box>

            {/* Card Body */}
            <Box sx={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Image Upload */}
                {bannerImage ? (
                    <Box
                        sx={{
                            position: 'relative', borderRadius: '10px', overflow: 'hidden',
                            border: '1.5px solid #f0f0f0', transition: 'all 0.25s ease',
                            '&:hover': { borderColor: '#E7B437' },
                            '&:hover .replace-overlay': { opacity: 1 },
                        }}
                    >
                        <img
                            src={bannerImage} alt={title}
                            style={{ width: '100%', height: '125px', display: 'block', objectFit: 'cover' }}
                        />
                        <button
                            type="button" onClick={onRemoveImage}
                            style={{
                                position: 'absolute', top: '6px', right: '6px',
                                background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%',
                                width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                            <IoIosCloseCircle style={{ fontSize: '18px', color: '#d32f2f' }} />
                        </button>

                        <Box
                            className="replace-overlay"
                            sx={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                                padding: '16px 8px 6px', display: 'flex', justifyContent: 'center',
                                opacity: 0, transition: 'opacity 0.25s ease',
                            }}
                        >
                            <Button
                                component="label" variant="contained" size="small" disabled={loading}
                                sx={{
                                    borderRadius: '6px', textTransform: 'none', backgroundColor: '#E7B437',
                                    fontWeight: 700, fontSize: '11px', px: 2, py: 0.3,
                                    '&:hover': { backgroundColor: '#d4a02e' },
                                }}
                            >
                                {loading ? 'Uploading...' : 'Replace'}
                                <input type="file" hidden accept=".jpg,.png,.jpeg,.gif,.bmp,.webp,.svg" onChange={onImageUpload} />
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDropInternal}
                        sx={{
                            borderRadius: '10px',
                            border: `1.5px dashed ${isDragging ? '#E7B437' : '#d8d8d8'}`,
                            backgroundColor: isDragging ? '#fffef5' : '#fafafa',
                            padding: '16px 10px', transition: 'all 0.25s ease',
                            cursor: 'pointer', position: 'relative', textAlign: 'center',
                            '&:hover': { borderColor: '#E7B437', backgroundColor: '#fffef5' },
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
                                <CircularProgress size={26} sx={{ color: '#E7B437' }} />
                                <Typography sx={{ fontSize: '12px', color: '#888', fontWeight: 500 }}>Uploading...</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                <i className="fa fa-cloud-upload" aria-hidden="true" style={{ fontSize: '28px', color: '#E7B437', marginBottom: '2px' }}></i>
                                <Typography sx={{ fontSize: '13px', color: '#E7B437', fontWeight: 700 }}>Upload Image</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#aaa' }}>Drag & drop or click • PNG, JPG, WEBP</Typography>
                                {sizeHint && <Typography sx={{ fontSize: '10.5px', color: '#d4a02e', fontWeight: 600, mt: 0.3 }}>Size: {sizeHint}</Typography>}
                            </Box>
                        )}
                        <input
                            type="file" accept=".jpg,.png,.jpeg,.gif,.bmp,.webp,.svg"
                            onChange={onImageUpload} disabled={loading}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: loading ? 'not-allowed' : 'pointer' }}
                        />
                    </Box>
                )}

                {/* Controls Row */}
                <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Visibility Toggle */}
                    <Box
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            border: '1px solid #f0f0f0', borderRadius: '8px', padding: '6px 10px',
                            backgroundColor: '#fafafa', transition: 'all 0.2s ease',
                            '&:hover': { borderColor: '#E7B437' },
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: '#555' }}>Show on App</Typography>
                            <Typography sx={{ fontSize: '9.5px', color: '#aaa' }}>
                                {isVisible ? 'Currently visible to users' : 'Hidden from users'}
                            </Typography>
                        </Box>
                        <Switch
                            checked={isVisible}
                            onChange={onVisibilityToggle}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#E7B437' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#E7B437' },
                            }}
                        />
                    </Box>

                    {/* Redirect Dropdown */}
                    {!hideRedirect && (
                        <Box
                            sx={{
                                border: '1px solid #f0f0f0', borderRadius: '8px', padding: '8px 10px',
                                backgroundColor: '#fafafa', transition: 'all 0.2s ease',
                                '&:hover': { borderColor: '#E7B437' },
                            }}
                        >
                            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>
                                Redirect To
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel id={`redirect-label-${bannerIndex}`} sx={{ fontSize: '12px', '&.Mui-focused': { color: '#E7B437' } }}>
                                    Select Screen
                                </InputLabel>
                                <Select
                                    labelId={`redirect-label-${bannerIndex}`}
                                    value={redirectScreen}
                                    label="Select Screen"
                                    onChange={(e) => onRedirectChange(e.target.value)}
                                    sx={{
                                        borderRadius: '7px', backgroundColor: '#fff', fontSize: '12.5px',
                                        '& .MuiSelect-select': { py: 0.8 },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E7B437' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E7B437' },
                                    }}
                                >
                                    {REDIRECT_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '12.5px' }}>{opt.label}</MenuItem>
                                    ))}
                                    {showCustomUrlOption && <MenuItem value="custom" sx={{ fontSize: '12.5px' }}>Custom URL</MenuItem>}
                                </Select>
                            </FormControl>

                            {redirectScreen === 'custom' && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Enter Custom URL"
                                    value={customUrl || ''}
                                    onChange={(e) => onCustomUrlChange && onCustomUrlChange(e.target.value)}
                                    sx={{
                                        mt: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '7px', backgroundColor: '#fff', fontSize: '12.5px',
                                            '& input': { py: 0.8 },
                                            '& fieldset': { borderColor: '#e0e0e0' },
                                            '&:hover fieldset': { borderColor: '#E7B437' },
                                            '&.Mui-focused fieldset': { borderColor: '#E7B437' },
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Pooja Selector (shown when pooja_details is selected) */}
                    {showPoojaSelector && (
                        <Box
                            sx={{
                                border: '1px solid #f0e0a0',
                                borderRadius: '8px',
                                padding: '8px 10px',
                                backgroundColor: '#fffef5',
                                transition: 'all 0.2s ease',
                                '&:hover': { borderColor: '#E7B437' },
                            }}
                        >
                            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>
                                Select Pooja
                            </Typography>
                            <Autocomplete
                                size="small"
                                options={poojaList}
                                loading={poojaListLoading}
                                getOptionLabel={(opt) => opt.name || ''}
                                value={poojaList.find((p) => p._id === selectedPoojaId) || null}
                                onChange={(_, newVal) => onPoojaSelect && onPoojaSelect(newVal)}
                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search and select a pooja..."
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {poojaListLoading ? <CircularProgress size={14} sx={{ color: '#E7B437' }} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '7px', backgroundColor: '#fff', fontSize: '12.5px',
                                                '& input': { py: 0.6 },
                                                '& fieldset': { borderColor: '#e0e0e0' },
                                                '&:hover fieldset': { borderColor: '#E7B437' },
                                                '&.Mui-focused fieldset': { borderColor: '#E7B437' },
                                            }
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option._id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography sx={{ fontSize: '12.5px', fontWeight: 600 }}>{option.name}</Typography>
                                            {option.basePrice && (
                                                <Typography sx={{ fontSize: '10.5px', color: '#999' }}>₹{option.basePrice}</Typography>
                                            )}
                                        </Box>
                                    </li>
                                )}
                                noOptionsText="No poojas found"
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

/* ─────────────────────────────────────────────
   Main Promo Banner Page (Side by Side)
   ───────────────────────────────────────────── */
function PromoBanner() {
    const dispatch = useDispatch();

    // Banner 1
    const [banner1Id, setBanner1Id] = useState('');
    const [banner1Image, setBanner1Image] = useState(null);
    const [banner1Visible, setBanner1Visible] = useState(false);
    const [banner1Redirect, setBanner1Redirect] = useState('');
    const [banner1Loading, setBanner1Loading] = useState(false);
    const [banner1PoojaId, setBanner1PoojaId] = useState('');

    // Banner 2
    const [banner2Id, setBanner2Id] = useState('');
    const [banner2Image, setBanner2Image] = useState(null);
    const [banner2Visible, setBanner2Visible] = useState(false);
    const [banner2Redirect, setBanner2Redirect] = useState('');
    const [banner2Loading, setBanner2Loading] = useState(false);
    const [banner2PoojaId, setBanner2PoojaId] = useState('');

    // Banner 3
    const [banner3Id, setBanner3Id] = useState('');
    const [banner3Image, setBanner3Image] = useState(null);
    const [banner3Visible, setBanner3Visible] = useState(false);
    const [banner3Redirect, setBanner3Redirect] = useState('');
    const [banner3Loading, setBanner3Loading] = useState(false);
    const [banner3PoojaId, setBanner3PoojaId] = useState('');

    // Banner 4
    const [banner4Id, setBanner4Id] = useState('');
    const [banner4Image, setBanner4Image] = useState(null);
    const [banner4Visible, setBanner4Visible] = useState(false);
    const [banner4Redirect, setBanner4Redirect] = useState('');
    const [banner4Loading, setBanner4Loading] = useState(false);
    const [banner4PoojaId, setBanner4PoojaId] = useState('');


    // Astro Banner
    const [astroId, setAstroId] = useState('');
    const [astroImage, setAstroImage] = useState(null);
    const [astroVisible, setAstroVisible] = useState(false);
    const [astroRedirect, setAstroRedirect] = useState('');
    const [astroLoading, setAstroLoading] = useState(false);
    const [astroPoojaId, setAstroPoojaId] = useState('');
    const [astroCustomUrl, setAstroCustomUrl] = useState('');

    // Free Chat Promo Banner
    const [freeChatImage, setFreeChatImage] = useState(null);
    const [freeChatVisible, setFreeChatVisible] = useState(false);
    const [freeChatLoading, setFreeChatLoading] = useState(false);

    // Free Chat Home Banner
    const [freeChatHomeImage, setFreeChatHomeImage] = useState(null);
    const [freeChatHomeVisible, setFreeChatHomeVisible] = useState(false);
    const [freeChatHomeLoading, setFreeChatHomeLoading] = useState(false);

    // Pooja Banner
    const [poojaBanners, setPoojaBanners] = useState([]);

    // Drag and Drop state for Pooja Banners (section: 'pooja-promo')
    const [draggedPoojaIndex, setDraggedPoojaIndex] = useState(null);
    const [dragOverPoojaIndex, setDragOverPoojaIndex] = useState(null);

    // Position Reorder Confirmation Dialog state
    const [promoReorderConfirmDialog, setPromoReorderConfirmDialog] = useState({
        open: false,
        section: null,
        sourceIndex: null,
        targetIndex: null,
        sourceLabel: '',
        targetLabel: ''
    });
    const [updatingPromoPosition, setUpdatingPromoPosition] = useState(false);

    // Section-wide Quick Reorder Overlay (HUD / Popup)
    const [reorderOverlay, setReorderOverlay] = useState({
        open: false,
        section: 'pooja-promo',
        title: 'Pooja Banners',
        sourceIndex: null,
        dragOverIndex: null,
        isManualModal: false
    });

    // Pooja list for pooja_details redirect
    const [poojaList, setPoojaList] = useState([]);
    const [poojaListLoading, setPoojaListLoading] = useState(false);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, bannerId: null });

    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(true);

    const fetchPoojaList = useCallback(async () => {
        try {
            setPoojaListLoading(true);
            const token = localStorage.getItem('astroVani-admin-token');
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/pooja?page=1&limit=500`,
                { headers: { Authorization: token } }
            );
            const data = response.data;
            const list = Array.isArray(data?.data?.data)
                ? data.data.data
                : Array.isArray(data?.data)
                    ? data.data
                    : Array.isArray(data)
                        ? data
                        : [];
            setPoojaList(list);
        } catch (err) {
            console.error('Error fetching pooja list:', err);
        } finally {
            setPoojaListLoading(false);
        }
    }, []);

    const handlePoojaImageUpload = async (event, id) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please select a valid image file.'); return; }
        if (!validateFile(file, 'image')) return;

        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                setPoojaBanners(prev => prev.map(b => b.id === id ? { ...b, image: uploadedUrl.toString() } : b));
                toast.success('Image uploaded!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Error uploading image.');
        } finally {
            event.target.value = '';
        }
    };

    const removePoojaImage = (id) => {
        setPoojaBanners(prev => prev.map(b => b.id === id ? { ...b, image: null } : b));
    };

    const togglePoojaBanner = (id, isActive) => {
        setPoojaBanners(prev => prev.map(b => b.id === id ? { ...b, isActive } : b));
        updateBannerToggle(id, isActive, () => {
            setPoojaBanners(prev => prev.map(b => b.id === id ? { ...b, isActive: !isActive } : b));
        });
    };

    const updatePoojaRedirect = (id, val) => {
        setPoojaBanners(prev => prev.map(b =>
            b.id === id
                ? { ...b, redirectFor: val, poojaId: val === 'pooja_details' ? (b.poojaId || '') : '', redirectionUrl: val === 'pooja_details' ? (b.poojaId ? `/pooja-details/${b.poojaId}` : '') : '' }
                : b
        ));
        // Fetch pooja list when pooja_details is selected
        if (val === 'pooja_details' && poojaList.length === 0) {
            fetchPoojaList();
        }
    };

    const updatePoojaBannerPooja = (id, pooja) => {
        if (!pooja) {
            setPoojaBanners(prev => prev.map(b => b.id === id ? { ...b, poojaId: '', redirectionUrl: '' } : b));
            return;
        }
        setPoojaBanners(prev => prev.map(b =>
            b.id === id
                ? { ...b, poojaId: pooja._id, redirectionUrl: `/pooja-details/${pooja._id}` }
                : b
        ));
    };

    const handlePoojaDrop = async (e, id) => {
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please drop a valid image file.'); return; }
        if (!validateFile(file, 'image')) return;

        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                setPoojaBanners(prev => prev.map(b => b.id === id ? { ...b, image: uploadedUrl.toString() } : b));
                toast.success('Image uploaded!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Error uploading image.');
        }
    };

    const addNewPoojaBanner = async () => {
        try {
            const token = localStorage.getItem('astroVani-admin-token');
            const response = await axios.post(
                `${process.env.REACT_APP_BASEURL}/admin/promo-banners/pooja`,
                { type: 'pooja', isActive: false, redirectFor: 'recharge_pack', imageUrl: '', position: poojaBanners.length + 1 },
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            );
            if (response.data?.success) {
                toast.success('Pooja banner added!');
                fetchPromoBanners();
            }
        } catch (err) {
            console.error('Error adding banner:', err);
            toast.error(err?.response?.data?.message || 'Failed to add new Pooja banner');
        }
    };

    const deletePoojaBanner = async () => {
        const id = deleteDialog.bannerId;
        if (!id) return;
        try {
            const token = localStorage.getItem('astroVani-admin-token');
            await axios.delete(
                `${process.env.REACT_APP_BASEURL}/admin/promo-banners/${id}`,
                { headers: { Authorization: token } }
            );
            toast.success('Pooja banner deleted!');
            fetchPromoBanners();
        } catch (err) {
            console.error('Error deleting banner:', err);
            toast.error(err?.response?.data?.message || 'Failed to delete Pooja banner');
        } finally {
            setDeleteDialog({ open: false, bannerId: null });
        }
    };

    // Fetch existing data
    const fetchPromoBanners = useCallback(async () => {
        try {
            const token = localStorage.getItem('astroVani-admin-token');
            if (!token) return;

            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/promo-banners`,
                { headers: { Authorization: token } }
            );

            if (response.data?.success) {
                const data = Array.isArray(response.data?.data) ? response.data.data : [];
                const promo1 = data.find((item) => item?.type === 'promo1');
                const promo2 = data.find((item) => item?.type === 'promo2');
                const promo3 = data.find((item) => item?.type === 'promo3');
                const promo4 = data.find((item) => item?.type === 'promo4');
                const astro = data.find((item) => item?.type === 'astro');
                const poojaData = data.filter(
                    (item) => item?.type === "pooja"
                );
                poojaData.sort((a, b) => {
                    if (a.position !== undefined && b.position !== undefined) {
                        return a.position - b.position;
                    }
                    return 0;
                });

                if (promo1) {
                    setBanner1Id(promo1._id || '');
                    setBanner1Image(promo1.imageUrl || null);
                    setBanner1Visible(promo1.isActive ?? false);
                    setBanner1Redirect(promo1.redirectFor || '');
                    setBanner1PoojaId(promo1.poojaId || '');
                }
                if (promo2) {
                    setBanner2Id(promo2._id || '');
                    setBanner2Image(promo2.imageUrl || null);
                    setBanner2Visible(promo2.isActive ?? false);
                    setBanner2Redirect(promo2.redirectFor || '');
                    setBanner2PoojaId(promo2.poojaId || '');
                }
                if (promo3) {
                    setBanner3Id(promo3._id || '');
                    setBanner3Image(promo3.imageUrl || null);
                    setBanner3Visible(promo3.isActive ?? false);
                    setBanner3Redirect(promo3.redirectFor || '');
                    setBanner3PoojaId(promo3.poojaId || '');
                }
                if (promo4) {
                    setBanner4Id(promo4._id || '');
                    setBanner4Image(promo4.imageUrl || null);
                    setBanner4Visible(promo4.isActive ?? false);
                    setBanner4Redirect(promo4.redirectFor || '');
                    setBanner4PoojaId(promo4.poojaId || '');
                }
                if (astro) {
                    setAstroId(astro._id || '');
                    setAstroImage(astro.imageUrl || null);
                    setAstroVisible(astro.isActive ?? false);
                    setAstroRedirect(astro.redirectFor || 'custom');
                    setAstroPoojaId(astro.poojaId || '');
                    if (astro.redirectFor === 'custom' || !astro.redirectFor) {
                        setAstroCustomUrl(astro.redirectionUrl || '');
                    }
                }

                try {
                    const fcResponse = await axios.get(
                        `${process.env.REACT_APP_BASEURL}/admin/promo-freechat-banner`,
                        { headers: { Authorization: token } }
                    );
                    if (fcResponse.data?.success && fcResponse.data.data) {
                        const fcData = fcResponse.data.data;

                        if (fcData.freeChat) {
                            setFreeChatImage(fcData.freeChat.imageUrl || null);
                            setFreeChatVisible(fcData.freeChat.isActive ?? false);
                        }

                        if (fcData.freeChatHome) {
                            setFreeChatHomeImage(fcData.freeChatHome.imageUrl || null);
                            setFreeChatHomeVisible(fcData.freeChatHome.isActive ?? false);
                        }
                    }
                } catch (e) { console.error('Error fetching freechat banner', e); }

                setPoojaBanners(
                    poojaData.map((item, index) => ({
                        id: item._id,
                        image: item.imageUrl || "",
                        isActive: item.isActive || false,
                        redirectFor: item.redirectFor || "",
                        poojaId: item.poojaId || "",
                        redirectionUrl: item.redirectionUrl || "",
                        type: item?.type,
                        position: item.position ?? (index + 1)
                    }))
                );

            }
        } catch (err) {
            console.error('Error fetching promo banners:', err);
        } finally {
            setFetching(false);
        }
    }, []);

    useEffect(() => {
        fetchPromoBanners();
        fetchPoojaList();
    }, [fetchPromoBanners, fetchPoojaList]);

    // Generic image upload
    const handleImageUpload = async (event, setImage, setLoading) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please select a valid image file.'); return; }
        if (!validateFile(file, 'image')) return;

        setLoading(true);
        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                setImage(uploadedUrl?.toString());
                toast.success('Image uploaded!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Error uploading image.');
        } finally {
            setLoading(false);
            event.target.value = '';
        }
    };

    // Generic drop
    const handleDrop = async (e, setImage, setLoading) => {
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please drop a valid image file.'); return; }
        if (!validateFile(file, 'image')) return;

        setLoading(true);
        try {
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            if (uploadedUrl) {
                setImage(uploadedUrl?.toString());
                toast.success('Image uploaded!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Error uploading image.');
        } finally {
            setLoading(false);
        }
    };

    // --- Pooja Banner Drag & Drop (Section: 'pooja-promo') ---
    const handlePoojaDragStart = (e, index) => {
        const banner = poojaBanners[index];
        if (!banner?.isActive) {
            e.preventDefault();
            toast.info('Disabled banners cannot be reordered. Only Live banners can be swapped.');
            return;
        }
        if (!banner?.image) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('text/plain', JSON.stringify({ section: 'pooja-promo', index }));
        e.dataTransfer.effectAllowed = 'move';
        setDraggedPoojaIndex(index);
        setTimeout(() => {
            setReorderOverlay({
                open: true,
                section: 'pooja-promo',
                title: 'Pooja Banners',
                sourceIndex: index,
                dragOverIndex: null,
                isManualModal: false
            });
        }, 60);
    };

    const handlePoojaDragOver = (e, index) => {
        // Only allow drop if target has an image and is Live
        if (!poojaBanners[index]?.image || !poojaBanners[index]?.isActive) return;

        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverPoojaIndex !== index) {
            setDragOverPoojaIndex(index);
        }
    };

    const handlePoojaDragLeave = (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragOverPoojaIndex(null);
    };

    const handlePoojaDragEnd = () => {
        setDraggedPoojaIndex(null);
        setDragOverPoojaIndex(null);
        setReorderOverlay(prev => prev.isManualModal ? prev : { ...prev, open: false, sourceIndex: null, dragOverIndex: null });
    };

    const handlePoojaDropReorder = (e, targetIndex) => {
        e.preventDefault();
        setDragOverPoojaIndex(null);
        setDraggedPoojaIndex(null);

        let data;
        try {
            data = JSON.parse(e.dataTransfer.getData('text/plain'));
        } catch {
            return;
        }

        if (!data || data.section !== 'pooja-promo') {
            toast.error('Banners can only be reordered within the same section!');
            return;
        }

        const sourceIndex = data.index;
        if (sourceIndex === targetIndex || sourceIndex === null || sourceIndex === undefined) return;

        // Validation: both source and target must have images
        if (!poojaBanners[sourceIndex]?.image || !poojaBanners[targetIndex]?.image) {
            toast.error('Cannot reorder: Drop is only allowed on filled slots with images.');
            return;
        }

        // Validation: only Live banners can be swapped
        if (!poojaBanners[sourceIndex]?.isActive || !poojaBanners[targetIndex]?.isActive) {
            toast.error('Cannot reorder: Only Live banners can be swapped.');
            return;
        }

        // Open confirmation dialog
        setPromoReorderConfirmDialog({
            open: true,
            section: 'pooja-promo',
            sourceIndex,
            targetIndex,
            sourceLabel: `Pooja Banner #${sourceIndex + 1}`,
            targetLabel: `Pooja Banner #${targetIndex + 1}`
        });
    };

    // --- Overlay Reorder Helpers ---
    const openManualReorderModal = () => {
        setReorderOverlay({
            open: true,
            section: 'pooja-promo',
            title: 'Pooja Banners',
            sourceIndex: null,
            dragOverIndex: null,
            isManualModal: true
        });
    };

    const handleOverlayDrop = (targetIndex) => {
        const { sourceIndex } = reorderOverlay;
        if (sourceIndex === null || sourceIndex === undefined || sourceIndex === targetIndex) return;

        if (!poojaBanners[sourceIndex]?.image || !poojaBanners[targetIndex]?.image) {
            toast.error('Cannot reorder: Both slots must have an image.');
            return;
        }
        if (!poojaBanners[sourceIndex]?.isActive || !poojaBanners[targetIndex]?.isActive) {
            toast.error('Cannot reorder: Only Live banners can be swapped.');
            return;
        }
        setReorderOverlay({ open: false, section: null, title: '', sourceIndex: null, dragOverIndex: null, isManualModal: false });
        setPromoReorderConfirmDialog({
            open: true,
            section: 'pooja-promo',
            sourceIndex,
            targetIndex,
            sourceLabel: `Pooja Banner #${sourceIndex + 1}`,
            targetLabel: `Pooja Banner #${targetIndex + 1}`
        });
    };

    const handleOverlayTileClick = (clickedIndex) => {
        if (!reorderOverlay.isManualModal) return;
        const clickedBanner = poojaBanners[clickedIndex];

        if (reorderOverlay.sourceIndex === null) {
            if (!clickedBanner?.isActive) {
                toast.info('Disabled (Off) banners cannot be reordered. Only Live banners can be swapped.');
                return;
            }
            if (!clickedBanner?.image) {
                toast.info('Empty slots cannot be reordered.');
                return;
            }
            setReorderOverlay(prev => ({ ...prev, sourceIndex: clickedIndex }));
        } else if (reorderOverlay.sourceIndex === clickedIndex) {
            setReorderOverlay(prev => ({ ...prev, sourceIndex: null }));
        } else {
            if (!clickedBanner?.isActive) {
                toast.info('Cannot swap with a disabled banner. Only Live banners can be swapped.');
                return;
            }
            if (!clickedBanner?.image) {
                toast.info('Cannot swap with an empty slot.');
                return;
            }
            handleOverlayDrop(clickedIndex);
        }
    };

    const handleConfirmPromoReorder = async () => {
        const { section, sourceIndex, targetIndex } = promoReorderConfirmDialog;
        if (sourceIndex === null || targetIndex === null) return;

        setUpdatingPromoPosition(true);
        const token = localStorage.getItem('astroVani-admin-token');
        if (!token) {
            toast.error('Authentication token not found.');
            setUpdatingPromoPosition(false);
            return;
        }

        try {
            if (section === 'pooja-promo') {
                const firstBanner = poojaBanners[sourceIndex];
                const secondBanner = poojaBanners[targetIndex];

                if (!firstBanner?.isActive || !secondBanner?.isActive) {
                    toast.error('Only Live banners can be swapped.');
                    setUpdatingPromoPosition(false);
                    return;
                }

                const firstId = firstBanner?.id || firstBanner?._id;
                const secondId = secondBanner?.id || secondBanner?._id;

                if (!firstId || !secondId) {
                    toast.error('Banner ID not found for reordering.');
                    setUpdatingPromoPosition(false);
                    return;
                }

                const response = await axios.patch(
                    `${process.env.REACT_APP_BASEURL}/admin/promo-banners/pooja/reorder`,
                    {
                        firstId,
                        secondId,
                    },
                    {
                        headers: {
                            Authorization: token,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (response.data?.success || response.status === 200) {
                    await fetchPromoBanners();
                    toast.success(response.data?.message || 'Pooja banner positions updated and saved successfully!');
                    setPromoReorderConfirmDialog({ open: false, section: null, sourceIndex: null, targetIndex: null, sourceLabel: '', targetLabel: '' });
                } else {
                    toast.error(response.data?.message || 'Failed to reorder banners');
                }
            }
        } catch (err) {
            console.error('Reorder save error:', err);
            toast.error(err?.response?.data?.message || 'Error updating banner position.');
        } finally {
            setUpdatingPromoPosition(false);
        }
    };

    // Save all
    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('astroVani-admin-token');
            if (!token) { toast.error('Authentication token not found.'); return; }

            const createPayload = (type, image, redirectFor, isActive, poojaId, customUrl) => {
                const payload = {
                    type,
                    image,
                    redirectFor,
                    isActive,
                };
                if (redirectFor === 'pooja_details') {
                    if (poojaId) {
                        payload.poojaId = poojaId;
                        payload.redirectionUrl = `/pooja-details/${poojaId}`;
                    }
                } else if (redirectFor === 'custom' && customUrl) {
                    payload.redirectionUrl = customUrl;
                } else {
                    payload.redirectionUrl = '';
                }
                return payload;
            };

            const payloads = [
                createPayload('promo1', banner1Image, banner1Redirect, banner1Visible, banner1PoojaId),
                createPayload('promo2', banner2Image, banner2Redirect, banner2Visible, banner2PoojaId),
                createPayload('promo3', banner3Image, banner3Redirect, banner3Visible, banner3PoojaId),
                createPayload('promo4', banner4Image, banner4Redirect, banner4Visible, banner4PoojaId),
                createPayload('astro', astroImage, astroRedirect, astroVisible, astroPoojaId, astroCustomUrl)
            ];

            const requests = payloads.map((payload) => axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/promo-banners`,
                payload,
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            ));

            const poojaRequests = poojaBanners.map((banner, index) => {
                const payload = {
                    imageUrl: banner.image,
                    redirectFor: banner.redirectFor,
                    isActive: banner.isActive,
                    position: index + 1,
                };
                if (banner.redirectFor === 'pooja_details') {
                    if (banner.poojaId) {
                        payload.poojaId = banner.poojaId;
                        payload.redirectionUrl = banner.redirectionUrl || `/pooja-details/${banner.poojaId}`;
                    }
                } else {
                    payload.redirectionUrl = banner.redirectionUrl || '';
                }
                return axios.patch(
                    `${process.env.REACT_APP_BASEURL}/admin/promo-banners/pooja/${banner.id}`,
                    payload,
                    { headers: { Authorization: token, 'Content-Type': 'application/json' } }
                );
            });

            const freeChatRequest = axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/promo-freechat`,
                { imageUrl: freeChatImage || "", isActive: freeChatVisible, type: "freeChat" },
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            );

            const freeChatHomeRequest = axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/promo-freechat`,
                { imageUrl: freeChatHomeImage || "", isActive: freeChatHomeVisible, type: "freeChatHome" },
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            );

            await Promise.all([...requests, ...poojaRequests, freeChatRequest, freeChatHomeRequest]);

            toast.success('Promo banners updated successfully!');
        } catch (err) {
            console.error('Save error:', err);
            toast.error(err?.response?.data?.message || 'Error updating promo banners.');
        } finally {
            setSaving(false);
        }
    };

    const updateFreeChatBannerToggle = async (isActive, type, rollback) => {
        const token = localStorage.getItem('astroVani-admin-token');
        if (!token) {
            toast.error('Authentication token not found.');
            rollback();
            return;
        }
        try {
            await axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/promo-freechat`,
                { imageUrl: type === 'freeChatHome' ? (freeChatHomeImage || "") : (freeChatImage || ""), isActive, type },
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            );
            toast.success(`${type === 'freeChatHome' ? 'Free Chat Home Banner' : 'Free Chat Promo Banner'} ${isActive ? 'enabled' : 'disabled'} successfully!`);
        } catch (err) {
            console.error('Toggle error:', err);
            rollback();
            toast.error(err?.response?.data?.message || 'Failed to update Free Chat banner status.');
        }
    };

    const updateBannerToggle = async (bannerId, isActive, rollback) => {
        const token = localStorage.getItem('astroVani-admin-token');
        if (!token) {
            toast.error('Authentication token not found.');
            rollback();
            return;
        }
        if (!bannerId) {
            toast.error('Banner id not found.');
            rollback();
            return;
        }

        try {
            await axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/promo-banners/${bannerId}`,
                { isActive },
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            );
            toast.success(`Banner ${isActive ? 'enabled' : 'disabled'} successfully!`);
        } catch (err) {
            console.error('Toggle error:', err);
            rollback();
            toast.error(err?.response?.data?.message || 'Failed to update banner status.');
        }
    };

    return (
        <div className="container-xxl">
            {/* Header */}
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 2, py: 2.5, mb: 1,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#222', letterSpacing: '-0.3px' }}>
                        Promo Banners
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#999', mt: 0.3 }}>
                        Manage promotional banners displayed on the app home screen
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || banner1Loading || banner2Loading || banner3Loading || banner4Loading}
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                    sx={{
                        borderRadius: '10px', textTransform: 'none', px: 4, py: 1.1,
                        fontSize: '14px', fontWeight: 700,
                        background: 'linear-gradient(135deg, #E7B437, #d4a02e)',
                        color: '#fff',
                        boxShadow: '0 4px 16px rgba(231,180,55,0.3)',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #d4a02e, #c4920e)',
                            boxShadow: '0 6px 22px rgba(231,180,55,0.4)',
                            transform: 'translateY(-1px)',
                        },
                        '&:active': { transform: 'translateY(0)' },
                        '&:disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)', boxShadow: 'none' },
                    }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            {/* 3-Column Grid */}
            {fetching ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                    <Skeleton variant="rounded" height={340} sx={{ borderRadius: '16px' }} />
                    <Skeleton variant="rounded" height={340} sx={{ borderRadius: '16px' }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                        gap: 2.5,
                        alignItems: 'stretch',
                    }}
                >
                    {/* Banner 1 */}
                    <BannerCard
                        title="Promo Banner 1"
                        bannerIndex={1}
                        bannerImage={banner1Image}
                        isVisible={banner1Visible}
                        redirectScreen={banner1Redirect}
                        loading={banner1Loading}
                        onImageUpload={(e) => handleImageUpload(e, setBanner1Image, setBanner1Loading)}
                        onRemoveImage={() => setBanner1Image(null)}
                        onVisibilityToggle={(e) => {
                            const nextValue = e.target.checked;
                            const previousValue = banner1Visible;
                            setBanner1Visible(nextValue);
                            updateBannerToggle(banner1Id, nextValue, () => setBanner1Visible(previousValue));
                        }}
                        onRedirectChange={(val) => setBanner1Redirect(val)}
                        onDrop={(e) => handleDrop(e, setBanner1Image, setBanner1Loading)}
                        showPoojaSelector={banner1Redirect === 'pooja_details'}
                        poojaList={poojaList}
                        selectedPoojaId={banner1PoojaId}
                        onPoojaSelect={(val) => setBanner1PoojaId(val?._id || '')}
                        poojaListLoading={poojaListLoading}
                    />

                    {/* Banner 2 */}
                    <BannerCard
                        title="Promo Banner 2"
                        bannerIndex={2}
                        bannerImage={banner2Image}
                        isVisible={banner2Visible}
                        redirectScreen={banner2Redirect}
                        loading={banner2Loading}
                        onImageUpload={(e) => handleImageUpload(e, setBanner2Image, setBanner2Loading)}
                        onRemoveImage={() => setBanner2Image(null)}
                        onVisibilityToggle={(e) => {
                            const nextValue = e.target.checked;
                            const previousValue = banner2Visible;
                            setBanner2Visible(nextValue);
                            updateBannerToggle(banner2Id, nextValue, () => setBanner2Visible(previousValue));
                        }}
                        onRedirectChange={(val) => setBanner2Redirect(val)}
                        onDrop={(e) => handleDrop(e, setBanner2Image, setBanner2Loading)}
                        showPoojaSelector={banner2Redirect === 'pooja_details'}
                        poojaList={poojaList}
                        selectedPoojaId={banner2PoojaId}
                        onPoojaSelect={(val) => setBanner2PoojaId(val?._id || '')}
                        poojaListLoading={poojaListLoading}
                    />
                </Box>
            )}

            <Box sx={{ my: 3 }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#222', letterSpacing: '-0.3px' }}>
                    Connect Page Promo Banner
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#999', mt: 0.3 }}>
                    Manage promotional banners displayed on the app connect screen
                </Typography>
            </Box>

            {/* Connect Page Grid */}
            {fetching ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                    <Skeleton variant="rounded" height={340} sx={{ borderRadius: '16px' }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                        gap: 2.5,
                        alignItems: 'stretch',
                    }}
                >
                    {/* Banner 3 */}
                    <BannerCard
                        title="Promo Banner 3"
                        bannerIndex={3}
                        bannerImage={banner3Image}
                        isVisible={banner3Visible}
                        redirectScreen={banner3Redirect}
                        loading={banner3Loading}
                        onImageUpload={(e) => handleImageUpload(e, setBanner3Image, setBanner3Loading)}
                        onRemoveImage={() => setBanner3Image(null)}
                        onVisibilityToggle={(e) => {
                            const nextValue = e.target.checked;
                            const previousValue = banner3Visible;
                            setBanner3Visible(nextValue);
                            updateBannerToggle(banner3Id, nextValue, () => setBanner3Visible(previousValue));
                        }}
                        onRedirectChange={(val) => setBanner3Redirect(val)}
                        onDrop={(e) => handleDrop(e, setBanner3Image, setBanner3Loading)}
                        showPoojaSelector={banner3Redirect === 'pooja_details'}
                        poojaList={poojaList}
                        selectedPoojaId={banner3PoojaId}
                        onPoojaSelect={(val) => setBanner3PoojaId(val?._id || '')}
                        poojaListLoading={poojaListLoading}
                    />

                    {/* Banner 4 */}
                    {/* <BannerCard
                        title="Promo Banner 4"
                        bannerIndex={4}
                        bannerImage={banner4Image}
                        isVisible={banner4Visible}
                        redirectScreen={banner4Redirect}
                        loading={banner4Loading}
                        onImageUpload={(e) => handleImageUpload(e, setBanner4Image, setBanner4Loading)}
                        onRemoveImage={() => setBanner4Image(null)}
                        onVisibilityToggle={(e) => {
                            const nextValue = e.target.checked;
                            const previousValue = banner4Visible;
                            setBanner4Visible(nextValue);
                            updateBannerToggle(banner4Id, nextValue, () => setBanner4Visible(previousValue));
                        }}
                        onRedirectChange={(val) => setBanner4Redirect(val)}
                        onDrop={(e) => handleDrop(e, setBanner4Image, setBanner4Loading)}
                        showPoojaSelector={banner4Redirect === 'pooja_details'}
                        poojaList={poojaList}
                        selectedPoojaId={banner4PoojaId}
                        onPoojaSelect={(val) => setBanner4PoojaId(val?._id || '')}
                        poojaListLoading={poojaListLoading}
                    /> */}
                </Box>
            )}

            <Box sx={{ my: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography
                        sx={{
                            fontSize: '22px',
                            fontWeight: 800,
                            color: '#222',
                        }}
                    >
                        Astro Banners
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: '13px',
                            color: '#999',
                            mt: 0.3,
                        }}
                    >
                        Manage Astro banners
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                    gap: 2.5,
                }}
            >
                <BannerCard
                    title="Astro Banner"
                    bannerIndex={"A"}
                    bannerImage={astroImage}
                    isVisible={astroVisible}
                    redirectScreen={astroRedirect}
                    loading={astroLoading}
                    customUrl={astroCustomUrl}
                    onCustomUrlChange={setAstroCustomUrl}
                    onImageUpload={(e) =>
                        handleImageUpload(e, setAstroImage, setAstroLoading)
                    }
                    onRemoveImage={() => setAstroImage(null)}
                    onVisibilityToggle={(e) => {
                        const nextValue = e.target.checked;
                        const previousValue = astroVisible;
                        setAstroVisible(nextValue);
                        updateBannerToggle(astroId, nextValue, () => setAstroVisible(previousValue));
                    }}
                    onRedirectChange={(val) => setAstroRedirect(val)}
                    onDrop={(e) =>
                        handleDrop(e, setAstroImage, setAstroLoading)
                    }
                    showPoojaSelector={astroRedirect === 'pooja_details'}
                    poojaList={poojaList}
                    selectedPoojaId={astroPoojaId}
                    onPoojaSelect={(val) => setAstroPoojaId(val?._id || '')}
                    poojaListLoading={poojaListLoading}
                    showCustomUrlOption={true}
                />
            </Box>

            <Box sx={{ my: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#222' }}>
                        Free Chat & Home Promo Banners
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#999', mt: 0.3 }}>
                        Manage promotional banners displayed for Free Chat and Free Chat Home
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                <BannerCard
                    title="Free Chat Banner"
                    bannerIndex={"FC"}
                    bannerImage={freeChatImage}
                    isVisible={freeChatVisible}
                    loading={freeChatLoading}
                    hideRedirect={true}
                    onImageUpload={(e) => handleImageUpload(e, setFreeChatImage, setFreeChatLoading)}
                    onRemoveImage={() => setFreeChatImage(null)}
                    onVisibilityToggle={(e) => {
                        const nextValue = e.target.checked;
                        const previousValue = freeChatVisible;
                        setFreeChatVisible(nextValue);
                        updateFreeChatBannerToggle(nextValue, 'freeChat', () => setFreeChatVisible(previousValue));
                    }}
                    onDrop={(e) => handleDrop(e, setFreeChatImage, setFreeChatLoading)}
                />

                <BannerCard
                    title="Free Chat Home Banner"
                    bannerIndex={"FH"}
                    bannerImage={freeChatHomeImage}
                    isVisible={freeChatHomeVisible}
                    loading={freeChatHomeLoading}
                    hideRedirect={true}
                    onImageUpload={(e) => handleImageUpload(e, setFreeChatHomeImage, setFreeChatHomeLoading)}
                    onRemoveImage={() => setFreeChatHomeImage(null)}
                    onVisibilityToggle={(e) => {
                        const nextValue = e.target.checked;
                        const previousValue = freeChatHomeVisible;
                        setFreeChatHomeVisible(nextValue);
                        updateFreeChatBannerToggle(nextValue, 'freeChatHome', () => setFreeChatHomeVisible(previousValue));
                    }}
                    onDrop={(e) => handleDrop(e, setFreeChatHomeImage, setFreeChatHomeLoading)}
                />
            </Box>

            <Box sx={{ my: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography
                        sx={{
                            fontSize: '22px',
                            fontWeight: 800,
                            color: '#222',
                        }}
                    >
                        Pooja Banner
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: '13px',
                            color: '#999',
                            mt: 0.3,
                        }}
                    >
                        Manage Pooja banners
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {poojaBanners.length > 1 && (
                        <Button
                            variant="outlined"
                            startIcon={<MdSwapHoriz style={{ fontSize: '18px' }} />}
                            onClick={openManualReorderModal}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                color: '#E7B437',
                                borderColor: '#E7B437',
                                fontWeight: 700,
                                fontSize: '13px',
                                px: 2,
                                py: 0.8,
                                '&:hover': { borderColor: '#d4a02e', backgroundColor: '#fffef5' }
                            }}
                        >
                            Reorder Banners ({poojaBanners.length})
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={addNewPoojaBanner}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #E7B437, #d4a02e)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '13px',
                            px: 2.5,
                            py: 0.8,
                            boxShadow: '0 2px 8px rgba(231,180,55,0.3)',
                            '&:hover': { background: 'linear-gradient(135deg, #d4a02e, #c4920e)' }
                        }}
                    >
                        + Add Pooja Banner
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                    gap: 2.5,
                }}
            >
                {poojaBanners.map((banner, index) => (
                    <Box key={banner.id} sx={{ position: 'relative' }}>
                        <BannerCard
                            title={`Pooja Banner ${index + 1}`}
                            bannerIndex={banner?.position || index + 1}
                            bannerImage={banner.image}
                            isVisible={banner.isActive}
                            redirectScreen={banner.redirectFor}
                            loading={false}
                            onImageUpload={(e) =>
                                handlePoojaImageUpload(
                                    e,
                                    banner.id
                                )
                            }
                            onRemoveImage={() =>
                                removePoojaImage(banner.id)
                            }
                            onVisibilityToggle={(e) =>
                                togglePoojaBanner(
                                    banner.id,
                                    e.target.checked
                                )
                            }
                            onRedirectChange={(val) =>
                                updatePoojaRedirect(
                                    banner.id,
                                    val
                                )
                            }
                            onDrop={(e) =>
                                handlePoojaDrop(
                                    e,
                                    banner.id
                                )
                            }
                            showPoojaSelector={banner.redirectFor === 'pooja_details'}
                            poojaList={poojaList}
                            poojaListLoading={poojaListLoading}
                            selectedPoojaId={banner.poojaId || ''}
                            onPoojaSelect={(pooja) => updatePoojaBannerPooja(banner.id, pooja)}
                            sizeHint="Banner size should be 900px * 400px"
                            isDraggable={true}
                            onPositionDragStart={(e) => handlePoojaDragStart(e, index)}
                            onPositionDragEnd={handlePoojaDragEnd}
                            onPositionDragOver={(e) => handlePoojaDragOver(e, index)}
                            onPositionDragLeave={handlePoojaDragLeave}
                            onPositionDrop={(e) => handlePoojaDropReorder(e, index)}
                            isBeingDragged={draggedPoojaIndex === index}
                            isDragOver={dragOverPoojaIndex === index}
                        />
                        <Button
                            onClick={() => setDeleteDialog({ open: true, bannerId: banner.id })}
                            sx={{
                                position: 'absolute',
                                top: -12,
                                right: -12,
                                minWidth: 'auto',
                                p: 0.5,
                                color: '#d32f2f',
                                background: '#fff',
                                borderRadius: '50%',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 10,
                                '&:hover': { background: '#f5f5f5', transform: 'scale(1.1)' }
                            }}
                        >
                            <IoIosCloseCircle style={{ fontSize: '24px' }} />
                        </Button>
                    </Box>
                ))}
            </Box>

            {/* Quick Reorder HUD / Layer / Modal */}
            {reorderOverlay.open && (
                <Box
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => { e.preventDefault(); }}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.72)',
                        backdropFilter: 'blur(8px)',
                        zIndex: 1400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 1.5, md: 3 },
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '1100px',
                            maxHeight: '88vh',
                            backgroundColor: '#fff',
                            borderRadius: '20px',
                            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            border: '2px solid rgba(231,180,55,0.4)',
                        }}
                    >
                        {/* Overlay Header */}
                        <Box
                            sx={{
                                background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 100%)',
                                px: 3,
                                py: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid #f2e7c4',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #E7B437, #d4a02e)',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        boxShadow: '0 2px 8px rgba(231,180,55,0.4)'
                                    }}
                                >
                                    <MdSwapHoriz />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: '#222' }}>
                                        Pooja Banners — Quick Position Reorder
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>
                                        {reorderOverlay.sourceIndex !== null
                                            ? `⚡ Dragging Slot #${reorderOverlay.sourceIndex + 1}. Drop onto any Live slot below to instantly swap!`
                                            : `💡 Click or drag between any two Live slots to swap positions. Disabled (Off) banners cannot be swapped.`}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => setReorderOverlay({ open: false, section: null, title: '', sourceIndex: null, dragOverIndex: null, isManualModal: false })}
                                sx={{ color: '#888', '&:hover': { color: '#d32f2f' } }}
                            >
                                <IoIosCloseCircle style={{ fontSize: '26px' }} />
                            </IconButton>
                        </Box>

                        {/* Overlay Grid Body */}
                        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1, backgroundColor: '#fcfcfc' }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'repeat(2, 1fr)',
                                        sm: 'repeat(3, 1fr)',
                                        md: 'repeat(4, 1fr)',
                                        lg: poojaBanners.length > 6 ? 'repeat(6, 1fr)' : 'repeat(4, 1fr)'
                                    },
                                    gap: 1.8,
                                }}
                            >
                                {poojaBanners.map((item, idx) => {
                                    const isSource = reorderOverlay.sourceIndex === idx;
                                    const isTarget = reorderOverlay.dragOverIndex === idx;
                                    const hasImage = Boolean(item.image);
                                    const isLive = Boolean(item.isActive);
                                    const canSwap = hasImage && isLive;

                                    return (
                                        <Box
                                            key={item.id}
                                            draggable={canSwap}
                                            onDragStart={(e) => {
                                                if (!canSwap) {
                                                    e.preventDefault();
                                                    return;
                                                }
                                                e.dataTransfer.setData('text/plain', JSON.stringify({ section: 'pooja-promo', index: idx }));
                                                e.dataTransfer.effectAllowed = 'move';
                                                setReorderOverlay(prev => ({ ...prev, sourceIndex: idx }));
                                            }}
                                            onDragOver={(e) => {
                                                if (!canSwap) return;
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (reorderOverlay.dragOverIndex !== idx) {
                                                    setReorderOverlay(prev => ({ ...prev, dragOverIndex: idx }));
                                                }
                                            }}
                                            onDragLeave={() => {
                                                if (reorderOverlay.dragOverIndex === idx) {
                                                    setReorderOverlay(prev => ({ ...prev, dragOverIndex: null }));
                                                }
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!canSwap) return;
                                                handleOverlayDrop(idx);
                                            }}
                                            onClick={() => handleOverlayTileClick(idx)}
                                            title={!isLive ? "Disabled banner (cannot be swapped)" : !hasImage ? "Empty slot" : "Click or drag to swap"}
                                            sx={{
                                                borderRadius: '12px',
                                                border: isTarget
                                                    ? '2px dashed #E7B437 !important'
                                                    : isSource
                                                        ? '2px solid #E7B437'
                                                        : !isLive
                                                            ? '1.5px solid #e0e0e0'
                                                            : '1.5px solid #e5e5e5',
                                                backgroundColor: isTarget ? '#fffef5' : isSource ? '#fff9e6' : !isLive ? '#f8f8f9' : '#fff',
                                                p: 1.2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: canSwap ? 'pointer' : 'not-allowed',
                                                transition: 'all 0.2s ease',
                                                transform: isTarget ? 'scale(1.05)' : isSource ? 'scale(0.98)' : 'none',
                                                boxShadow: isTarget ? '0 0 16px rgba(231,180,55,0.45)' : isSource ? '0 0 10px rgba(231,180,55,0.25)' : '0 2px 6px rgba(0,0,0,0.03)',
                                                opacity: !isLive ? 0.55 : !hasImage ? 0.6 : 1,
                                                position: 'relative',
                                                userSelect: 'none',
                                                '&:hover': canSwap ? {
                                                    borderColor: '#E7B437',
                                                    boxShadow: '0 4px 12px rgba(231,180,55,0.2)'
                                                } : {}
                                            }}
                                        >
                                            {/* Header row: badge + status */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '6px',
                                                        background: !isLive
                                                            ? '#9e9e9e'
                                                            : isSource
                                                                ? '#222'
                                                                : 'linear-gradient(135deg, #E7B437, #d4a02e)',
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '11px',
                                                        fontWeight: 800
                                                    }}
                                                >
                                                    #{item?.position || idx + 1}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box
                                                        sx={{
                                                            width: 7, height: 7, borderRadius: '50%',
                                                            bgcolor: isLive ? '#2e7d32' : '#9e9e9e'
                                                        }}
                                                    />
                                                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: isLive ? '#2e7d32' : '#777' }}>
                                                        {isLive ? 'Live' : 'Off'}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Thumbnail Image */}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '70px',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#f3f4f6',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    position: 'relative'
                                                }}
                                            >
                                                {hasImage ? (
                                                    <img
                                                        src={item.image}
                                                        alt={`pooja-${idx + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            filter: isLive ? 'none' : 'grayscale(60%)',
                                                            opacity: isLive ? 1 : 0.75
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: '10px', color: '#aaa', fontWeight: 600 }}>
                                                        Empty Slot
                                                    </Typography>
                                                )}

                                                {!isLive && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            right: 4,
                                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                                            color: '#fff',
                                                            fontSize: '8.5px',
                                                            fontWeight: 700,
                                                            px: 0.6,
                                                            py: 0.15,
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        Disabled
                                                    </Box>
                                                )}

                                                {isTarget && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            backgroundColor: 'rgba(231,180,55,0.85)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#fff',
                                                            fontSize: '11px',
                                                            fontWeight: 800,
                                                            textAlign: 'center',
                                                            px: 1
                                                        }}
                                                    >
                                                        Drop to Swap!
                                                    </Box>
                                                )}

                                                {isSource && !isTarget && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            left: 4,
                                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                                            color: '#fff',
                                                            fontSize: '9px',
                                                            fontWeight: 700,
                                                            px: 0.8,
                                                            py: 0.2,
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        Selected
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Title and redirect */}
                                            <Box sx={{ mt: 0.8 }}>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isLive ? '#333' : '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.title || `Pooja Banner ${item?.position ?? (idx + 1)}`}
                                                </Typography>
                                                <Typography sx={{ fontSize: '9px', color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.redirectFor === 'pooja_details'
                                                        ? (poojaList.find(p => p._id === item.poojaId)?.name || 'Pooja Details')
                                                        : (item.redirectFor || 'No redirect')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Overlay Footer */}
                        <Box
                            sx={{
                                px: 3,
                                py: 1.5,
                                backgroundColor: '#fcfcfc',
                                borderTop: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Typography sx={{ fontSize: '11.5px', color: '#888', fontStyle: 'italic' }}>
                                💡 Tip: Only Live banners can be dragged or swapped. Disabled (Off) banners will remain fixed in place.
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => setReorderOverlay({ open: false, section: null, title: '', sourceIndex: null, dragOverIndex: null, isManualModal: false })}
                                sx={{ color: '#666', textTransform: 'none', fontWeight: 700, fontSize: '12.5px' }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, bannerId: null })}
                PaperProps={{
                    sx: { borderRadius: '16px', padding: '8px', minWidth: '320px' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#333', fontSize: '18px' }}>
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#666', fontSize: '14px' }}>
                        Are you sure you want to delete this banner? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, bannerId: null })}
                        sx={{ color: '#888', textTransform: 'none', fontWeight: 700 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={deletePoojaBanner}
                        variant="contained"
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none',
                            backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Position Reorder Confirmation Dialog */}
            <Dialog
                open={promoReorderConfirmDialog.open}
                onClose={() => !updatingPromoPosition && setPromoReorderConfirmDialog({ open: false, section: null, sourceIndex: null, targetIndex: null, sourceLabel: '', targetLabel: '' })}
                PaperProps={{
                    sx: { borderRadius: '18px', padding: '10px', minWidth: '380px' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#222', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '20px' }}>🔄</span> Confirm Position Change
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#555', fontSize: '14.5px', mb: 2 }}>
                        Are you sure you want to change banner positions?
                    </Typography>
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 100%)',
                            borderRadius: '12px',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            border: '1px solid #f0e0a0',
                            fontWeight: 700,
                            fontSize: '15px',
                            color: '#333'
                        }}
                    >
                        <Chip
                            label={promoReorderConfirmDialog.sourceLabel}
                            sx={{ fontWeight: 800, background: '#E7B437', color: '#fff', fontSize: '13px' }}
                        />
                        <span style={{ fontSize: '20px', color: '#888' }}>⇄</span>
                        <Chip
                            label={promoReorderConfirmDialog.targetLabel}
                            sx={{ fontWeight: 800, background: '#E7B437', color: '#fff', fontSize: '13px' }}
                        />
                    </Box>
                    <Typography sx={{ color: '#888', fontSize: '12px', mt: 2 }}>
                        Confirming will immediately update the banner positions and save the changes.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button
                        onClick={() => setPromoReorderConfirmDialog({ open: false, section: null, sourceIndex: null, targetIndex: null, sourceLabel: '', targetLabel: '' })}
                        disabled={updatingPromoPosition}
                        sx={{ color: '#888', textTransform: 'none', fontWeight: 700 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmPromoReorder}
                        variant="contained"
                        disabled={updatingPromoPosition}
                        startIcon={updatingPromoPosition ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '8px',
                            px: 3,
                            background: 'linear-gradient(135deg, #E7B437, #d4a02e)',
                            '&:hover': { background: 'linear-gradient(135deg, #d4a02e, #c4920e)' }
                        }}
                    >
                        {updatingPromoPosition ? 'Updating...' : 'Confirm & Update'}
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
}

export default PromoBanner;
